<script setup lang="ts">
import { computed } from 'vue'
import UiText from './UiText.vue'

export type UiSwitchSize = 'sm' | 'md'

export interface UiSwitchProps {
  label?: string
  name?: string
  disabled?: boolean
  size?: UiSwitchSize
}

const props = withDefaults(defineProps<UiSwitchProps>(), {
  disabled: false,
  size: 'md',
})

const model = defineModel<boolean>({ default: false })

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return {
        track: 'w-9 h-5',
        knob: 'w-4 h-4',
        knobTranslateOn: 'translate-x-4',
      } as const
    case 'md':
    default:
      return {
        track: 'w-10 h-6',
        knob: 'w-5 h-5',
        knobTranslateOn: 'translate-x-4',
      } as const
  }
})

const isChecked = computed(() => Boolean(model.value))

function toggle() {
  if (props.disabled) return
  model.value = !model.value
}

function onKeydown(e: KeyboardEvent) {
  if (props.disabled) return
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    toggle()
  }
}
</script>

<template>
  <button
    type="button"
    role="switch"
    :aria-checked="isChecked"
    :aria-disabled="props.disabled || undefined"
    :disabled="props.disabled"
    class="inline-flex items-center gap-2 select-none"
    @click="toggle"
    @keydown="onKeydown"
  >
    <span class="relative inline-flex items-center">
      <input
        type="checkbox"
        class="hidden"
        :name="props.name"
        :disabled="props.disabled"
        v-model="model"
      />
      <span
        class="relative inline-flex items-center rounded-full transition-colors duration-150"
        :class="[
          sizeClasses.track,
          props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          isChecked ? 'bg-se-secondary-dark' : 'bg-se-gray-200',
        ]"
      >
        <span
          class="absolute left-0.5 inline-block rounded-full bg-white shadow transition-transform duration-150"
          :class="[
            sizeClasses.knob,
            isChecked ? sizeClasses.knobTranslateOn : 'translate-x-0',
          ]"
        />
      </span>
    </span>

    <UiText v-if="props.label" as="label" variant="label" class="text-left" :text="props.label" />
    <slot />
  </button>
</template>


